import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApolloError } from '@apollo/client';

import { LoggedInUserFragmentFragment, useVerifyLoggedInMutation } from 'data/__generated__';
import { hasAccessToken } from 'data/connection/storage';
import { logWarning } from 'helpers/logger';
import { useSentryUser } from 'hooks/useSentryUser';

/**
 * `unreachable` est le troisième état, et il compte : sans lui, un `verifyLoggedIn` qui échoue
 * pour une raison réseau retombe sur `user = null`, ce que les guards lisent comme « pas
 * authentifié » et qui envoie sur `/login` alors que la session est peut-être intacte. Tant qu'on
 * n'a pas eu de réponse du serveur, on ne sait pas — et on le dit, plutôt que de déconnecter.
 */
type AuthStatus = 'loading' | 'ready' | 'unreachable';

type AuthContextType = {
    user: LoggedInUserFragmentFragment | null;
    loading: boolean;
    unreachable: boolean;
    retry: () => void;
    setUser: (user: LoggedInUserFragmentFragment | null) => void;
};

const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    unreachable: false,
    retry: () => null,
    setUser: () => null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<LoggedInUserFragmentFragment | null>(null);
    const [status, setStatus] = useState<AuthStatus>('loading');
    const [verifyLoggedIn] = useVerifyLoggedInMutation();

    useSentryUser(user);

    const checkAuth = useCallback(async () => {
        if (!hasAccessToken()) {
            setStatus('ready');
            return;
        }

        setStatus('loading');

        try {
            const { data } = await verifyLoggedIn();
            const success = data?.verifyLoggedIn?.success;
            const verifiedUser = data?.verifyLoggedIn?.user;
            if (success && verifiedUser) {
                setUser(verifiedUser);
            }

            setStatus('ready');
        } catch (error) {
            // `useMutation` rejette sur erreur réseau comme sur erreur GraphQL (errorPolicy
            // 'none' par défaut, aucun `onError` ici). Sans ce catch, `status` restait sur
            // 'loading' pour toujours et les guards rendaient `null` : page blanche.
            //
            // Seule une erreur réseau veut dire « pas de réponse ». Une erreur GraphQL est une
            // réponse de l'API (déjà affichée par l'errorLink) : « Serveur injoignable » + un
            // Réessayer voué au même échec serait faux, on retombe sur le chemin non authentifié.
            if (!(error instanceof ApolloError && error.networkError)) {
                setStatus('ready');
                return;
            }

            logWarning({
                message: 'AuthProvider: verifyLoggedIn failed, API unreachable',
                contexts: { error: error.message },
            });
            setStatus('unreachable');
        }
    }, [verifyLoggedIn]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading: status === 'loading',
                unreachable: status === 'unreachable',
                retry: checkAuth,
                setUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    return useContext(AuthContext);
}
